import { cn, Icon, ListRow, NotificationDot } from "@iec-id/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NAV_SECTIONS, NOTIFICATIONS_SECTION } from "./nav-config";
import { SectionRowLink } from "./nav-link";

export type MobileNavProps = {
	/** Правая подпись строки по ключу раздела: «до 2027», «4». */
	meta?: Partial<Record<string, ReactNode>>;
	unreadCount?: number;
	onSignOut: () => void;
	/** Закрыть шторку после перехода. */
	onNavigate?: () => void;
};

/**
 * Список разделов для левой шторки. Не таб-бар: разделов восемь, в четыре
 * ячейки они не влезают.
 *
 * «Правовой центр» из списка убран (как в сайдбаре); документы — из меню
 * профиля. `NAV_SECTIONS` по-прежнему содержит legal для поиска.
 */
export function MobileNav({ meta, unreadCount = 0, onSignOut, onNavigate }: MobileNavProps) {
	const { t } = useTranslation("common");
	const sections = [
		...NAV_SECTIONS.filter((section) => section.key !== "legal"),
		NOTIFICATIONS_SECTION,
	];

	return (
		// Зазор между строками: сплошной список без него превращал подсветку
		// активного раздела в кусок сплошной серой заливки.
		<nav aria-label={t("nav.label")} className="flex flex-col gap-0.5">
			{sections.map((section) => (
				<SectionRowLink
					key={section.key}
					to={section.to}
					activeOptions={{ exact: section.to === "/" }}
					activeProps={{ className: "bg-sunken" }}
					onClick={onNavigate}
				>
					<ListRow
						density="default"
						icon={<Icon name={section.icon} />}
						title={t(section.labelKey)}
						meta={
							section.key === "notifications" && unreadCount > 0 ? (
								<NotificationDot value={unreadCount} />
							) : (
								<span className="t-meta text-fg-muted">{meta?.[section.key]}</span>
							)
						}
						trailing={<Icon name="ChevronRight" className="text-fg-muted" />}
					/>
				</SectionRowLink>
			))}
			<button
				type="button"
				onClick={onSignOut}
				className={cn(
					"iec-press-glyph mt-2 flex w-full cursor-pointer items-center gap-3.5 rounded-control px-3 py-3.5",
					"t-row text-left text-danger transition-colors duration-150 ease-smooth",
					"hover:bg-danger-soft",
					"focus-ring",
				)}
			>
				<Icon name="LogOut" />
				<span className="flex-grow">{t("nav.signOut")}</span>
			</button>
		</nav>
	);
}
